import { useTranslationWithMobile } from '../../hooks/useTranslationWithMobile';
import { Box, Text, Heading } from '@wix/design-system';
import React, { useEffect, useRef } from 'react';
import { ConfirmSmall } from '@wix/wix-ui-icons-common';
import AiAssistantIcon from './ai-assistant-light-theme.svg';
import cs from 'classnames';
import s from './ProgressBar.scss';
import { LOADER_COMPLETE_DELAY, LOADER_STEP_TIME } from '../../consts';
import {
  incrementStep,
  setProgressBarData,
  setSteps,
  useProgressBarData,
} from '../TwoColumnProgressBar/context/progressBarAtoms';

export type CustomStep = {
  label: string;
  icon: JSX.Element;
};

type Props = {
  steps?: CustomStep[];
  onComplete?: () => void;
  background?: string;
  isLoading?: boolean;
};

export const ProgressBar: React.FC<Props> = ({ steps, onComplete, background, isLoading = false }) => {
  const { t } = useTranslationWithMobile();
  const { currentStep, isComplete } = useProgressBarData();
  const componentRef = useRef<HTMLDivElement>(null);

  function scrollIntoView() {
    if (componentRef.current) {
      componentRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (componentRef.current) {
        try {
          componentRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        } catch (e) {
          // Fallback for browsers that don't support smooth scrolling
          componentRef.current.scrollIntoView(false);
        }
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    setTimeout(scrollIntoView, 100);
  }, []);

  useEffect(() => {
    setSteps(steps);
    setProgressBarData({ background });
  }, [steps, background]);

  useEffect(() => {
    if (!steps.length || isComplete) {
      return;
    }

    const timer = setTimeout(() => {
      incrementStep();
    }, LOADER_STEP_TIME);

    return () => clearTimeout(timer);
  }, [steps, currentStep, isComplete]);

  useEffect(() => {
    if (isComplete && onComplete && !isLoading) {
      // Add a small delay to ensure the progress bar animation reaches 100% before calling onComplete
      const timer = setTimeout(() => {
        onComplete();
      }, LOADER_COMPLETE_DELAY);

      return () => clearTimeout(timer);
    }
  }, [isComplete, onComplete, isLoading]);

  return (
    <Box
      ref={componentRef}
      className={s.root}
      direction="vertical"
      style={background ? { background } : undefined}
    >
      <Box className={s.icon}>
        <AiAssistantIcon width="40px" height="40px" />
      </Box>

      <Heading size="tiny" align="center" className={s.title}>
        {t('BUILDING_YOUR_SITE_LOADER')}
      </Heading>

      <Box className={s.steps} direction="vertical" gap={1}>
        {steps.map(({ icon, label }, index) => {
          const isStepActive = index === currentStep;
          const isStepCompleted = index < currentStep;
          const isStepPending = index > currentStep;

          return (
            <Box key={index} className={s.step} height="100%" gap={3}>
              <Box
                className={cs({
                  [s.stepCompletedIcon]: isStepCompleted,
                  [s.stepActiveIcon]: isStepActive,
                  [s.stepPendingIcon]: isStepPending,
                })}
              >
                {isStepCompleted ? <ConfirmSmall /> : icon}
              </Box>

              <Text
                size="small"
                className={cs(s.actionText, {
                  [s.gradientText]: !isStepPending,
                  [s.pending]: isStepPending,
                })}
              >
                {t(label)}
              </Text>
            </Box>
          );
        })}
      </Box>

      <Box className={s.progressContainer}>
        <Box className={s.progressBarBg}>
          <Box
            className={s.progressBarFg}
            style={{
              ['--progress-bar-width' as any]: `${(currentStep / steps.length) * 100}%`,
            }}
          />
        </Box>
      </Box>

      <Text size="small" className={s.subtitle}>
        {t('PLEASE_WAIT_WHILE_WE_SET_UP_YOUR_STORE')}
      </Text>
    </Box>
  );
};
